/**
 * Platform access control — merges platform rules with feature-owned access modules.
 * Feature routes/buttons: features/<name>/core/access (registered in configs/featureAccess).
 */

import { SUBSCRIPTION_TAB_ACCESS } from './subscriptionTabAccess';
import { mergeFeatureRoutes, mergeFeatureButtons } from './featureAccess';

const PLATFORM_ROUTE_ACCESS = {
  '/home': {
    departments: [],
    roles: [],
  },
  '/hr/employees': {
    departments: ['HR', 'Executive'],
    roles: [],
  },
  '/plan-images': {
    departments: ['Marketing', 'Executive'],
    roles: [],
  },
  '/user': {
    departments: [],
    roles: [],
  },
  '/dashboard/:id': {
    departments: [],
    roles: [],
  },
  '/dashboard/:id/diet-plan/:planId': {
    departments: [],
    roles: [],
  },
  '/dashboard/:id/workout-plan/:durationId': {
    departments: [],
    roles: [],
  },
  '/plan': {
    departments: ['Executive'],
    roles: [],
  },
  '/revenue': {
    departments: ['Executive'],
    roles: [],
  },
  // Flex Comms — Broadcasts are Executive-only: one send reaches the entire
  // client base, so it carries the same blast radius the old /notifications
  // campaign screen was gated for. Support admin (/support) and
  // Coaches/Oversight access is resolved at runtime by ms-agents API probe
  // (managers + executives, via OversightRoute). The agent console + ticket
  // threads are open to every employee so assignees can see their own tickets.
  '/messages': {
    departments: [],
    roles: [],
  },
  '/messages/:roomId': {
    departments: [],
    roles: [],
  },
  // Bookings is for the people who run call calendars: coaches and managers in
  // the Fitness/Nutritionist departments, plus executives (who pass via the
  // Executive short-circuit in hasRouteAccess). Within that, the page decides
  // what to show from the caller's own roles: a Manager gets the roster of
  // coaches assigned to them, a coach gets their own calendar read-only (their
  // manager publishes it), an executive gets every coach in the tenant.
  //
  // Titles mirror COACH_MANAGER_DEPARTMENTS / MANAGER_ROLE_TITLES /
  // COACH_ROLE_TITLES in pages/comms/Bookings/coachRoster.js — not imported,
  // because coachRoster imports utils/accessControl, which imports this file.
  //
  // This gate is navigation only; the backend still scopes every write itself
  // (resolve_caller_scope + may_act_for).
  '/bookings': {
    departments: ['Fitness', 'Nutritionist'],
    roles: ['Manager', 'Lead Coach', 'Coach'],
  },
  '/broadcasts': {
    departments: ['Executive'],
    roles: [],
  },
  '/broadcasts/new': {
    departments: ['Executive'],
    roles: [],
  },
  // No '/broadcasts/:id/edit' entry: v1_10_0 has no PATCH on a broadcast, so
  // the composer is create-only and that path is now a redirect to
  // '/broadcasts' rather than a PrivateRoute. Its gate is whatever gates the
  // list, and a rule here would never be consulted.
  '/broadcasts/:id/analytics': {
    departments: ['Executive'],
    roles: [],
  },
  '/support': {
    departments: [],
    roles: [],
  },
  '/support/console': {
    departments: [],
    roles: [],
  },
  '/support/tickets/:id': {
    departments: [],
    roles: [],
  },
  // Coach analytics is for managers and executives only — coaches never get the
  // nav item even if the oversight probe lets them through. The sidebar still
  // requires that probe too (staffOnly); the backend scopes the data itself.
  '/coaches': {
    departments: [],
    roles: ['Manager'],
  },
  '/coaches/:coachId': {
    departments: [],
    roles: ['Manager'],
  },
  '/oversight/:roomId': {
    departments: [],
    roles: [],
  },
};

export const ROUTE_ACCESS = mergeFeatureRoutes(PLATFORM_ROUTE_ACCESS);

export const DASHBOARD_ACCESS = {
  'emp-dashboard': {
    departments: ['HR', 'Executive'],
    roles: [],
  },
};

const PLATFORM_BUTTON_PERMISSIONS = {
  'emp-dashboard': {
    'verify-employee': {
      departments: ['HR', 'Executive'],
      roles: [],
    },
    'update-role': {
      departments: ['HR', 'Executive'],
      roles: [],
    },
    'assign-manager': {
      departments: ['HR', 'Executive'],
      roles: [],
    },
    'delete-employee': {
      departments: ['Executive'],
      roles: [],
    },
  },
  'user-dashboard': {
    'view-subscriptions-tab': SUBSCRIPTION_TAB_ACCESS,
  },
};

export const BUTTON_PERMISSIONS = mergeFeatureButtons(PLATFORM_BUTTON_PERMISSIONS);

export const EXECUTIVE_DEPARTMENT = 'Executive';
